import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X } from 'lucide-react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { useChat } from 'features/Chat/ChatContext'
import { useChatPopup } from 'features/Chat/ChatPopupContext'
import getEventIdForReceiverIds from 'features/Chat/utils/getEventIdForReceiverIds'
import Composer from './Composer'

/**
 * New-message screen: an iOS "To:" field over the user's partners, then the
 * same composer as a thread.
 *
 * Does not reuse ChatAddressBar, which logs on every render and calls hooks
 * before an early return.
 */
const ComposeView = ({ isActive }: { isActive: boolean }) => {
	const dispatch = useDispatch()
	const { showThread } = useChatPopup()
	const {
		state: {
			chatHeaders,
			userId,
			currentEventId,
			currentReceiverIds,
			chatInput,
			pendingAttachments,
			isSendingMessage,
		},
		actions: {
			openThread,
			handleAutocomplete,
			handleChatInput,
			handleSendChatMessage,
			handleInsertEmoji,
			handleAddAttachments,
			handleRemoveAttachment,
		},
	} = useChat()

	const [query, setQuery] = useState('')
	const [selected, setSelected] = useState<any[]>([])

	const { entities, ids } = useSelector((state: any) => state.myPartners)

	useEffect(() => {
		dispatch(initializeMyPartners() as any)
	}, [dispatch])

	// Sending the first message creates the thread and sets the pointer. Follow
	// it, so the user lands in the conversation they just started instead of
	// being left on a compose screen with their message nowhere in sight.
	useEffect(() => {
		if (isActive && currentEventId) showThread()
	}, [isActive, currentEventId, showThread])

	const partners = useMemo(() => (ids || []).map(id => entities[id]).filter(Boolean), [ids, entities])

	const suggestions = useMemo(() => {
		const q = query.trim().toLowerCase()
		return partners.filter((p: any) => {
			const name = p.partner?.name || ''
			if (selected.some((s: any) => s.partner?._id === p.partner?._id)) return false
			return !q || name.toLowerCase().includes(q)
		})
	}, [partners, query, selected])

	const commit = (next: any[]) => {
		setSelected(next)
		setQuery('')
		// Mirrors the MUI Autocomplete signature the context action expects.
		handleAutocomplete(null, next)

		// If a conversation with exactly these people already exists, open it
		// rather than composing into a blank screen. The message would have
		// been appended to that thread on send either way, so showing an empty
		// compose view first only hid the history it was about to join.
		const existing = getEventIdForReceiverIds(
			chatHeaders,
			next.map((p: any) => p.partner?._id).filter(Boolean),
			userId,
		)
		if (existing) {
			openThread(existing)
			showThread()
		}
	}

	return (
		<div data-testid="chat-popup-compose" className="flex min-h-0 flex-1 flex-col">
			<div className="shrink-0 border-b border-gray-200 px-3 py-2">
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="text-[15px] text-gray-400">To:</span>
					{selected.map((p: any) => (
						<span
							key={p.partner?._id}
							className="inline-flex items-center gap-1 rounded-full bg-[#0080fe]/10 px-2 py-0.5 text-[13px] text-[#0080fe]"
						>
							{p.partner?.name}
							<button
								type="button"
								aria-label={`Remove ${p.partner?.name}`}
								onClick={() => commit(selected.filter((s: any) => s.partner?._id !== p.partner?._id))}
								className="hover:opacity-70"
							>
								<X className="h-3 w-3" />
							</button>
						</span>
					))}
					<input
						value={query}
						onChange={e => setQuery(e.target.value)}
						autoFocus
						aria-label="Recipients"
						placeholder={selected.length === 0 ? 'Search partners' : ''}
						className="min-w-[120px] flex-1 bg-transparent text-[15px] outline-none placeholder:text-gray-400"
					/>
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
				{suggestions.length === 0 ? (
					<p className="px-4 py-6 text-center text-[13px] text-gray-400">
						{partners.length === 0 ? 'No partners yet.' : 'No matches.'}
					</p>
				) : (
					<ul className="m-0 list-none divide-y divide-gray-100 p-0">
						{suggestions.map((p: any) => (
							<li key={p.partner?._id} className="list-none">
								<button
									type="button"
									onClick={() => commit([...selected, p])}
									className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
								>
									<Avatar className="h-9 w-9 shrink-0">
										<AvatarImage src={p.partner?.image} alt={p.partner?.name} />
										<AvatarFallback>
											{p.partner?.name?.charAt(0).toUpperCase() || '?'}
										</AvatarFallback>
									</Avatar>
									<span className="min-w-0 flex-1 truncate text-[15px] text-gray-900">
										{p.partner?.name}
									</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			<div className={cn(currentReceiverIds.length === 0 && 'pointer-events-none opacity-50')}>
				<Composer
					value={chatInput}
					isDisabled={isSendingMessage || currentReceiverIds.length === 0}
					pendingAttachments={pendingAttachments}
					onChange={handleChatInput}
					onSend={handleSendChatMessage}
					onInsertEmoji={handleInsertEmoji}
					onAddAttachments={handleAddAttachments}
					onRemoveAttachment={handleRemoveAttachment}
				/>
			</div>
		</div>
	)
}

export default ComposeView
